// Board fx-pay / fx-size: once a sale goes through, show "Paid ₹x · bill no",
// the receipt choices and New sale, instead of dropping straight back to an
// empty Browse screen. Read-only: it watches the cart the payment screen was
// open on, and when that bill number is replaced it looks the bill up in the
// till's saved orders. Only a bill that was actually saved shows here, so
// Back to bill, park or cancel never do. The paper receipt has already printed
// through the usual completion path, unless the cashier chose No receipt on the
// payment screen; then this offers Print receipt. It also adds e-receipt and
// gift receipt.
import React, { useEffect, useRef, useState } from "react";
import db from "../../../database";
import { fashionPosService } from "../../../services";
import { formatMoney } from "../../../utils/fashion/money";
import { buildGiftReceiptHtml, giftReceiptLines, printGiftReceipt } from "../../../utils/fashion/giftReceipt";
import { buildSendReceiptMutation, canSendReceipt, readSendReceipt, RECEIPT_CHANNELS } from "../../../utils/fashion/digitalReceipt";
import { isDigitalReceipt, readReceiptChoice, skipsPaperReceipt, usableReceiptChoice } from "../../../utils/fashion/receiptChoice";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";
import PrintController from "../../../lib/printer/printController";
import "./payV4.css";

// The order row can land a moment after the cart resets; look a few times.
const LOOKUP_DELAYS = [0, 400, 1200, 3000];
// Bills already auto-sent on this device, so a remount never sends twice.
const autoSentBills = new Set();

export async function findSavedOrder(dbAdapter, documentno) {
  if (!documentno) return null;
  try {
    const rows = (await dbAdapter.orders.where("documentno").equals(documentno).toArray()) || [];
    return rows[rows.length - 1] || null;
  } catch {
    return null;
  }
}

export function useSaleDone(cart, paying, { dbAdapter = db, delays = LOOKUP_DELAYS } = {}) {
  const watched = useRef(null);
  const lookup = useRef({ id: 0, timers: [] });
  const [done, setDone] = useState(null);
  const documentno = cart?.documentno;
  const itemCount = (cart?.items || []).length;

  // The lookup outlives the render that started it: completing a sale replaces
  // the bill number and closes the payment screen in separate renders, and a
  // per-effect cleanup would cancel the lookup in between.
  const stopLookup = () => {
    lookup.current.id += 1;
    lookup.current.timers.forEach(clearTimeout);
    lookup.current.timers = [];
  };
  const startLookup = (billNo) => {
    stopLookup();
    const id = lookup.current.id;
    lookup.current.timers = delays.map((ms) =>
      setTimeout(async () => {
        if (lookup.current.id !== id) return;
        const row = await findSavedOrder(dbAdapter, billNo);
        if (row && lookup.current.id === id) {
          stopLookup();
          setDone(row);
        }
      }, ms)
    );
  };
  useEffect(() => stopLookup, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const prev = watched.current;
    if (prev && prev !== documentno) {
      // The bill the payment screen was open on has been replaced.
      watched.current = null;
      startLookup(prev);
    }
    if (paying && documentno && watched.current !== documentno) {
      watched.current = documentno;
      setDone(null);
    }
  }, [documentno, paying]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scanning or picking the next item starts the next sale.
  useEffect(() => {
    if (itemCount > 0) setDone(null);
  }, [itemCount]);

  return [done, () => setDone(null)];
}

const custOf = (order) => (order?.customer && typeof order.customer === "object" ? order.customer : null);

export default function SaleDone({ order, tillData, onNew, printReceipt = PrintController }) {
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState(null);
  const newRef = useRef(null);
  useEffect(() => newRef.current?.focus(), []);

  const isRefund = (parseFloat(order?.total) || 0) < 0;
  // A lay-away or credit bill is saved with part of it paid; show what was taken.
  const partPaid = order?.layAway === "Y" || order?.creditSale === "Y";
  const total = Math.abs(parseFloat(partPaid ? order?.paid : order?.total) || 0);
  const change = parseFloat(order?.change) || 0;
  const c = custOf(order);
  const mobile = c?.mobileNo || c?.mobile || "";
  const canSend = canSendReceipt(order);
  const canGift = !isRefund && giftReceiptLines(order).length > 0;
  // Read once: the paper receipt was skipped for this sale.
  const [noPaper] = useState(() => skipsPaperReceipt(tillData, c));
  // A till with eBill on already sends the receipt when the bill is booked.
  const eBill = usePOSConfigStore((s) => s.posConfig?.eBill) === "Y";
  // WhatsApp or Email chosen on the payment screen: send it once, as if tapped here.
  const [autoSend] = useState(() => {
    const choice = usableReceiptChoice(readReceiptChoice(tillData), c);
    return noPaper && !eBill && isDigitalReceipt(choice) ? RECEIPT_CHANNELS.find((ch) => ch.value === choice) || null : null;
  });

  const run = async (key, fn) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy("");
    }
  };
  const sendOnce = async (via, label) => {
    let res;
    try {
      res = readSendReceipt(await fashionPosService(buildSendReceiptMutation(order.sOrderID, via)));
    } catch {
      res = { ok: false, message: "Couldn't send the receipt. Check the connection and try again." };
    }
    setNote(res.ok ? { tone: "ok", text: `${res.message || `Receipt sent on ${label}`}.` } : { tone: "bad", text: res.message });
    return res.ok;
  };
  const send = (via, label) => run(via, () => sendOnce(via, label));
  const gift = () =>
    run("gift", async () => {
      const res = await printGiftReceipt(buildGiftReceiptHtml(order, tillData));
      setNote(res.ok ? { tone: "ok", text: "Gift receipt sent to the printer." } : { tone: "bad", text: res.message });
    });

  // Auto-send once per bill. The paper print was skipped for it, so when the
  // send fails (bill not on the server yet, no connection) the receipt prints instead.
  useEffect(() => {
    const billNo = order?.documentno;
    if (!autoSend || !billNo || autoSentBills.has(billNo)) return;
    autoSentBills.add(billNo);
    run(autoSend.value, async () => {
      const ok = canSend && (await sendOnce(autoSend.value, autoSend.label));
      if (ok) return;
      try {
        await printReceipt(order, order);
        setNote({ tone: "warn", text: `Couldn't send the ${autoSend.label} receipt, so it printed instead.` });
      } catch {
        setNote({ tone: "bad", text: `Couldn't send the ${autoSend.label} receipt or reach the printer. Tap Print receipt.` });
      }
    });
    // run and sendOnce are recreated each render; this runs once per bill
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend, canSend, order?.documentno]);

  const paper = () =>
    run("paper", async () => {
      try {
        await printReceipt(order, order);
        setNote({ tone: "ok", text: "Receipt sent to the printer." });
      } catch {
        setNote({ tone: "bad", text: "Couldn't reach the receipt printer. Check it and try again." });
      }
    });

  return (
    <section className="fp-done" aria-label="Sale complete">
      <div className="fp-done-card" role="status">
        <svg className="fp-done-tick" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M7.5 12.5l3 3 6-6.5" />
        </svg>
        <h2 className="fp-done-title">
          {isRefund ? "Refunded" : partPaid ? "Advance paid" : "Paid"} <span className="fp-num">{formatMoney(total, tillData)}</span>
        </h2>
        <span className="fp-muted fp-num">{order?.documentno}</span>
        {change > 0 && (
          <p className="fp-done-change">
            Give back <b className="fp-num">{formatMoney(change, tillData)}</b>
          </p>
        )}
      </div>
      {(noPaper || canSend || canGift) && (
        <div className="fp-done-rcpt">
          <span className="fp-lbl">Receipt{canSend && mobile ? ` to ${mobile}` : ""}</span>
          <div className="fp-done-seg">
            {noPaper && (
              <button type="button" className="fp-btn-ghost" disabled={Boolean(busy)} onClick={paper}>
                {busy === "paper" ? "Printing…" : "Print receipt"}
              </button>
            )}
            {canSend &&
              RECEIPT_CHANNELS.map((ch) => (
                <button key={ch.value} type="button" className="fp-btn-ghost" disabled={Boolean(busy)} onClick={() => send(ch.value, ch.label)}>
                  {busy === ch.value ? "Sending…" : ch.label}
                </button>
              ))}
            {canGift && (
              <button type="button" className="fp-btn-ghost" disabled={Boolean(busy)} onClick={gift}>
                {busy === "gift" ? "Printing…" : "Gift receipt (no prices)"}
              </button>
            )}
          </div>
        </div>
      )}
      {note && (
        <p className={`fp-done-note is-${note.tone}`} role="status">
          {note.text}
        </p>
      )}
      <button ref={newRef} type="button" className="fp-btn-primary fp-done-new" onClick={onNew}>
        New sale
      </button>
    </section>
  );
}
